import React, { useMemo } from 'react';
import { fromJS, List } from 'immutable';
import { useAppSelector } from 'flow/hooks/useRedux';
import { TicketKey } from 'flow/types/EntityKeys';
import {
    ConversationMessageKey,
    EmailRecipient,
} from 'flow/types/TicketEventData';
import { TypedMap } from 'flow/types/TypedMap';
import {
    selectEmailMessageCcRecipients,
    selectEmailMessageSenderName,
    selectEmailMessageToRecipients,
    selectEventSender,
} from 'flow/selectors/TicketEventSelectors';
import { MenuItems } from '@headlessui/react';
import MenuItemEmailIdentities from './MenuItemEmailIdentities';

interface Props {
    ticketKey: TicketKey;
    eventKey: ConversationMessageKey;
}

const MenuSenderAndRecipients = ({ ticketKey, eventKey }: Props) => {
    const sender = useAppSelector((state) =>
        selectEventSender(state, ticketKey, eventKey),
    );
    const senderName = useAppSelector((state) =>
        selectEmailMessageSenderName(state, ticketKey, eventKey),
    );

    const senderIdentityList: List<TypedMap<EmailRecipient>> = useMemo(
        () => fromJS([{ name: senderName, address: sender }]),
        [sender, senderName],
    );

    const toRecipients = useAppSelector((state) =>
        selectEmailMessageToRecipients(state, ticketKey, eventKey),
    );
    const ccRecipients = useAppSelector((state) =>
        selectEmailMessageCcRecipients(state, ticketKey, eventKey),
    );

    return (
        <MenuItems
            anchor="bottom end"
            className="z-20 max-h-96 w-max rounded-md bg-white p-2 text-xs shadow-lg dark:bg-gray-200"
        >
            <MenuItemEmailIdentities
                identities={senderIdentityList}
                titlei18Key="from"
                highlightName={true}
            />
            <MenuItemEmailIdentities
                identities={toRecipients}
                titlei18Key="call.to"
            />
            <MenuItemEmailIdentities
                identities={ccRecipients}
                titlei18Key="tickets.email.cc"
            />
        </MenuItems>
    );
};

export default React.memo(MenuSenderAndRecipients);
